import { useState } from 'react';
import VideoPlayer from '@/components/feature/VideoPlayer';
import { useLang } from '../../../hooks/useLang';
import RWASelfTest from './RWASelfTest';

const assetTypes = [
  { icon: 'ri-building-line', name: '房地产', desc: '住宅、商业、工业地产代币化，降低投资门槛，拆分为最小单位。英国已全面推进不动产链上确权，香港沙盒项目多以不动产RWA为主。', tag: '最成熟', tvl: '$500亿+', color: '#34D399', pilot: '香港绿地集团、领展REIT试点' },
  { icon: 'ri-line-chart-line', name: '债券与票据', desc: '国债、企业债、应收账款等固定收益资产，机构最常见选择。BlackRock BUIDL基金已代币化，规模超50亿美元。', tag: '高流动性', tvl: '$200亿+', color: '#60A5FA', pilot: 'BlackRock BUIDL、富兰克林链上基金' },
  { icon: 'ri-oil-line', name: '大宗商品', desc: '黄金、石油、农产品等实物资产上链，实时估值透明。Paxos Gold（PAXG）是全球最大黄金代币，1:1锚定伦敦金库黄金。', tag: '对冲通胀', tvl: '$50亿+', color: '#F59E0B', pilot: 'Paxos Gold (PAXG)、Tether Gold (XAUt)' },
  { icon: 'ri-stock-line', name: '股权与基金', desc: '私募股权、VC基金份额链上流通，解决传统PE流动性难题。Hamilton Lane已将超20亿美元基金份额代币化，二级市场流动性提升80%。', tag: '高收益', tvl: '$80亿+', color: '#A78BFA', pilot: 'Hamilton Lane、KKR Fund代币化' },
  { icon: 'ri-artboard-line', name: '艺术品与收藏', desc: '名画、古董、限量版藏品分级化，让普通人参与顶级资产。Masterworks已将毕加索、巴斯奎特等作品代币化，累计销售额超7亿美元。', tag: '新兴市场', tvl: '$5亿+', color: '#FB923C', pilot: 'Masterworks、Mintus艺术代币化平台' },
  { icon: 'ri-leaf-line', name: '碳信用与ESG', desc: 'REDD+、蓝碳、工业减排等碳资产代币化，接入绿色DeFi。Toucan Protocol已将超过2000万吨碳信用代币化，KlimaDAO持有超1000万吨CCER。', tag: 'ESG', tvl: '$20亿+', color: '#4ADE80', pilot: 'Toucan Protocol、KlimaDAO' },
  { icon: 'ri-truck-line', name: '供应链金融', desc: '应收账款、存货、贸易融资票据代币化，解决中小企业融资难题。链上应收账款可多级流转，无需银行保理，成本降低60%以上。2月6日新政明确支持供应链金融RWA化。', tag: '政策重点', tvl: '$30亿+', color: '#06B6D4', pilot: '蚂蚁链供应链金融平台、招商银行贸易融资' },
  { icon: 'ri-sun-line', name: '新能源/光伏', desc: '光伏电站、风电场、储能设施未来现金流代币化，将绿色能源资产带入全球资本市场。协鑫能科光伏RWA已在香港完成首批合规发行，总规模3.2亿港元。', tag: '2月6日新政', tvl: '$15亿+', color: '#F97316', pilot: '协鑫能科香港RWA、隆基绿能海外代币化' },
  { icon: 'ri-building-2-line', name: '基础设施/REITs', desc: '高速公路、桥梁、数据中心、港口等基础设施未来收益权代币化，实现"基建REITs 2.0"。门槛从传统REITs的数百万元降至数千元，流动性大幅提升。', tag: '政府支持', tvl: '$40亿+', color: '#8B5CF6', pilot: '亚投行基础设施代币化项目、香港机场基础设施RWA' },
  { icon: 'ri-file-shield-line', name: '证件与凭证上链', desc: '学位证书、房产证、营业执照、专业资格证等各类证件和凭证，通过区块链实现不可篡改的电子存证，是RWA体系中非融资类资产上链的重要形态。', tag: '未来趋势', tvl: '规模测算中', color: '#EC4899', pilot: '英国土地注册区块链、教育部学历链上验证' },
];

const myths = [
  { myth: 'RWA就是炒币、发虚拟货币', reality: '完全不同。RWA代表真实资产的链上权益凭证，底层是真实世界资产。合规的RWA须持牌发行、受证监会监管，与无监管的虚拟货币截然不同。监管机构将两者明确区分，不可混淆。', icon: 'ri-coin-line', dangerLevel: '高风险误区' },
  { myth: '境内企业可以直接发行RWA', reality: '严禁。合法路径只有一条：境内资产确权 → 证监会备案 → 香港SPV → SFC持牌机构发行 → 仅限香港专业投资者（净资产≥800万港元）认购。任何绕过这一路径的"境内发行"都是违法行为。', icon: 'ri-bank-line', dangerLevel: '违法误区' },
  { myth: 'RWA适合普通散户投资', reality: '目前不适合。香港合规框架下RWA代币属于证券型代币，仅限专业投资者（PI）认购，个人须满足净资产≥800万港元。面向公众发售属严重违规，可追刑事责任。', icon: 'ri-user-line', dangerLevel: '认知误区' },
  { myth: 'RWA代币化后资产100%安全', reality: '否。代币化解决的是流动性和可及性问题，不消除底层资产风险。底层资产违约、智能合约漏洞、发行方跑路等风险依然存在。须做独立尽职调查，不可盲信"链上等于安全"。', icon: 'ri-shield-line', dangerLevel: '认知误区' },
  { myth: 'RWA与DeFi/NFT是同一回事', reality: '根本不同。DeFi是链上金融协议生态，NFT是数字所有权凭证，而RWA是将现实资产引入区块链的合规框架。RWA必须具备监管合规性、法律确权、底层资产真实性，三者缺一不可，与投机性NFT和DeFi协议本质有别。', icon: 'ri-nft-line', dangerLevel: '认知误区' },
  { myth: '只有大企业才能做RWA', reality: '错误。2月6日新政明确支持中小企业通过供应链金融、碳资产、新能源等赛道参与RWA。香港已有持牌机构专门为中小企业设计低门槛发行方案，发行成本可控制在资产规模3%以内，门槛大幅降低。', icon: 'ri-building-4-line', dangerLevel: '认知误区' },
  { myth: '智能合约自动执行就没有法律风险', reality: '大错特错。智能合约本身不具备法律约束力——它只是代码，不是合同。RWA的法律效力来源于底层的法律协议（香港或开曼法下的信托/基金合同），智能合约只是执行工具。合约漏洞、编程错误可能导致资产损失，且无法自动追偿。', icon: 'ri-code-s-slash-line', dangerLevel: '法律误区' },
  { myth: '持有RWA代币就等于拥有底层资产所有权', reality: '通常不是。大多数RWA代币代表的是"收益权"或"参与权"而非直接所有权。例如房产RWA代币持有者享有租金收益分配权，而非对房产的直接所有权。真正的所有权通常由信托结构持有，代币持有人是受益人而非产权人，需仔细阅读法律文件。', icon: 'ri-key-line', dangerLevel: '法律误区' },
];

const audienceValue = [
  { group: '零基础大众', icon: 'ri-user-smile-line', color: '#34D399', values: ['了解未来金融的底层逻辑', '提前建立认知优势', '理解亲友讨论的行业趋势', '为未来可能的合法参与做准备'] },
  { group: '在校学生', icon: 'ri-graduation-cap-line', color: '#60A5FA', values: ['发现高含金量的新兴岗位方向', '建立区块链金融知识体系', '为就业/考研方向提前布局', '参与顶尖院校相关课题研究'] },
  { group: '企业方', icon: 'ri-building-2-line', color: '#F59E0B', values: ['评估自有资产的代币化可行性', '了解香港合规发行全流程', '对接持牌机构和专业服务团队', '掌握行业上链的机遇与红线'] },
  { group: '合格投资者', icon: 'ri-vip-crown-line', color: '#A78BFA', values: ['了解RWA产品的风险收益特征', '甄别合规vs非合规RWA产品', '通过香港持牌机构合法认购', '多元化资产配置的新选择'] },
  { group: '区块链/RWA从业者', icon: 'ri-code-box-line', color: '#F97316', values: ['深化专业认知，建立系统知识体系', '了解全球监管动态与合规最新要求', '发现行业合作机会与商业机遇', '与同行共享最新政策与案例研究'] },
  { group: '转型入行者', icon: 'ri-exchange-line', color: '#06B6D4', values: ['了解RWA行业的岗位需求与能力要求', '掌握法律/金融/技术三维入行路径', '识别优质学习资源与从业机构', '规避行业陷阱，选择合规从业路径'] },
];

// 证件凭证上链专题数据
const credentialOnChainData = [
  {
    category: '房产与土地权证',
    icon: 'ri-home-2-line',
    color: '#34D399',
    globalCases: [
      { country: '英国', desc: 'HM Land Registry（英国土地注册处）2024年已将全英2650万份房产权证全部迁移至区块链系统，成为全球首个全国性房产权证上链的主要经济体。产权查询从原来的数天缩短至秒级完成，纠纷减少62%。' },
      { country: '瑞典', desc: '瑞典土地数据局与Chromaway合作，基于区块链实现房产交易全程数字化，签约、支付、过户可在一个平台完成，交易周期从3个月缩短至数天。' },
      { country: '格鲁吉亚', desc: '格鲁吉亚是全球最早推行区块链土地确权的国家，2016年即与Bitfury合作，将全国土地注册系统迁移至比特币区块链，有效遏制了产权欺诈。' },
    ],
    chinaCases: '中国不动产统一登记制度自2015年全面推进，不动产权证书已实现全国电子化。2024年，北京、上海、广州已开展不动产区块链存证试点，权证信息上链后可在政务平台秒级查验，正在推进跨部门共享。',
  },
  {
    category: '教育与学历证书',
    icon: 'ri-graduation-cap-line',
    color: '#60A5FA',
    globalCases: [
      { country: '麻省理工学院（MIT）', desc: 'MIT自2017年开始向毕业生提供区块链学位证书（基于Blockcerts标准），学生可向任何机构出示可一键验证的数字证书，无需联系学校出具证明。全球已有超过50所顶尖大学跟进。' },
      { country: '马耳他', desc: '马耳他政府2019年将全国高校的学历证书迁移至区块链系统，成为全球首个将学历证书全面上链的国家，留学生签证审查效率提升90%。' },
    ],
    chinaCases: '教育部"学信网"已实现高校学历学位证书全国电子化，2024年升级支持区块链存证，雇主可扫码一键核验，彻底杜绝学历造假。中国人民银行金融研究所等机构发放的继续教育证书也已实现链上存证。',
  },
  {
    category: '身份证件与企业证照',
    icon: 'ri-fingerprint-line',
    color: '#A78BFA',
    globalCases: [
      { country: '爱沙尼亚', desc: '爱沙尼亚是全球数字身份最领先的国家，e-ID系统基于区块链，公民几乎所有政府服务（投票、报税、公司注册、医疗记录）均可通过数字身份完成，每年节省行政成本约2%的GDP。' },
      { country: '迪拜', desc: '迪拜"区块链战略"将营业执照、贸易许可、签证等企业证照全部上链，企业开办时间从平均7天缩短至24小时以内，跨部门信息共享无需人工传递。' },
    ],
    chinaCases: '国家市场监管总局推进营业执照全国电子化，企业统一社会信用代码体系已实现跨部门数据共享。2023年以来，"亮照亮证"移动端核验全面推广，消费者可一键扫码核验商家资质，有效打击无证经营。',
  },
];

const thresholds2026 = [
  { label: '投资者门槛', value: '≥800万港元', desc: '个人净资产（专业投资者PI认定标准）', icon: 'ri-money-dollar-circle-line', color: '#F59E0B' },
  { label: '持牌机构', value: '12家VATP', desc: '香港SFC已批准的持牌虚拟资产交易平台', icon: 'ri-building-2-line', color: '#A78BFA' },
  { label: '备案时间', value: '≥20个工作日', desc: '证监会境内企业香港RWA发行备案最短周期', icon: 'ri-time-line', color: '#34D399' },
  { label: '合规成本', value: '50-200万港元', desc: '法律+SPV+审计+平台费用综合区间', icon: 'ri-file-list-3-line', color: '#FB923C' },
];

const blockchainBasics = [
  { step: '01', title: '区块链是什么？', desc: '分布式账本技术，数据不可篡改，多方共识验证，无需中心化机构信任背书。每个"区块"记录一批交易，通过密码学哈希链接，形成不可篡改的历史记录。', icon: 'ri-database-line' },
  { step: '02', title: '智能合约', desc: '部署在区块链上的自动执行代码，条件满足即触发，无需人工干预。RWA中负责自动分红、KYC白名单管理、转让限制等核心功能，代码即法律。', icon: 'ri-code-s-slash-line' },
  { step: '03', title: '代币标准', desc: 'ERC-20（同质化）、ERC-721（NFT非同质化）、ERC-1400（证券型，含KYC白名单、转让限制）、ERC-3525（半同质化，适合分级RWA产品）。', icon: 'ri-coin-line' },
  { step: '04', title: '预言机（Oracle）', desc: '区块链与现实世界的数据桥梁。将链下的资产价格、合规状态、利率等数据实时喂入链上合约。Chainlink是最主要的去中心化预言机，无oracle则RWA无法感知真实世界。', icon: 'ri-global-line' },
  { step: '05', title: 'DeFi与RWA的融合', desc: 'RWA作为真实收益资产进入DeFi借贷协议，解决DeFi"缺乏真实价值支撑"的问题。MakerDAO已持有超10亿美元RWA作为DAI储备，使DAI储蓄利率可达4-5%。', icon: 'ri-water-flash-line' },
  { step: '06', title: '托管与合规层', desc: '链上托管人（Custodian）保管私钥或合规文件；KYC/AML白名单合约限制持有者资格；转让限制合约确保代币只在合格投资者间流通，构成RWA合规基础设施。', icon: 'ri-shield-check-line' },
];

export default function RWAEntry() {
  const [showAllMythes, setShowAllMythes] = useState(false);
  const [activeCredential, setActiveCredential] = useState(0);
  const displayMythes = showAllMythes ? myths : myths.slice(0, 4);
  const { t } = useLang();

  return (
    <>
      {/* ======= 01 RWA入门 ======= */}
      <section id="rwa-entry" className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 lg:px-8">
          {/* Section header */}
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#7C3AED]/8 border border-[#7C3AED]/15 rounded-full mb-4">
              <i className="ri-seedling-line text-[#7C3AED] text-sm"></i>
              <span className="text-[#7C3AED] text-sm font-semibold">{t('01 · RWA 入门认知', '01 · RWA Introduction')}</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-black text-gray-900 mb-4">
              {t('什么是', 'What is ')}<span className="text-[#7C3AED]">{t('真实世界资产代币化', 'Real World Asset Tokenization')}</span>{t('？', '?')}
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
              {t(
                'RWA 将现实中的有形或无形资产通过区块链转化为数字代币，实现可拆分、可流转、可编程的资产管理方式。2026年，这是全球金融最重要的范式革命。',
                'RWA transforms tangible or intangible real-world assets into digital tokens via blockchain, enabling divisible, transferable, and programmable asset management. In 2026, this is the most important paradigm shift in global finance.'
              )}
            </p>
          </div>

          {/* RWA Core Visual */}
          <div
            className="rounded-2xl p-8 lg:p-10 mb-12"
            style={{ background: 'linear-gradient(135deg, #1A0050 0%, #2E1065 40%, #4C1D95 100%)' }}
          >
            <div className="grid lg:grid-cols-3 gap-6 items-center mb-8">
              {/* Left: Real Asset */}
              <div className="text-center lg:text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full mb-3 text-white/60 text-xs">
                  <i className="ri-home-2-line text-xs"></i>
                  {t('现实世界资产', 'Real World Assets')}
                </div>
                <div className="text-5xl font-black text-white mb-2">RWA</div>
                <div className="text-[#A78BFA] text-sm">Real World Assets</div>
                <div className="mt-3 space-y-1.5">
                  {[
                    t('房地产 / 债券 / 黄金', 'Real Estate / Bonds / Gold'),
                    t('股权 / 艺术品 / 碳信用', 'Equity / Art / Carbon Credits'),
                    t('应收账款 / 基础设施', 'Receivables / Infrastructure'),
                    t('供应链金融 / 新能源', 'Supply Chain Finance / Clean Energy'),
                    t('证件 / 凭证 / 数字身份', 'Certificates / Credentials / Digital ID'),
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2 text-white/60 text-xs">
                      <i className="ri-checkbox-circle-fill text-[#34D399] text-xs"></i>
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* Center: Arrow */}
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 flex items-center justify-center bg-[#F59E0B]/15 border border-[#F59E0B]/30 rounded-2xl">
                  <i className="ri-links-line text-[#F59E0B] text-3xl"></i>
                </div>
                <div className="text-center">
                  <div className="text-white font-bold text-sm mb-1">{t('区块链代币化技术', 'Blockchain Tokenization')}</div>
                  <div className="text-white/40 text-xs">{t('智能合约 · Oracle · 托管', 'Smart Contract · Oracle · Custody')}</div>
                  <div className="text-white/40 text-xs">{t('KYC/AML · 白名单合约', 'KYC/AML · Whitelist Contract')}</div>
                </div>
                <div className="flex items-center gap-2 text-white/30 text-xs">
                  <div className="h-px w-8 bg-white/20" />
                  <i className="ri-arrow-right-line"></i>
                  <div className="h-px w-8 bg-white/20" />
                </div>
              </div>

              {/* Right: Token */}
              <div className="text-center lg:text-right">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full mb-3 text-white/60 text-xs">
                  <i className="ri-coin-line text-xs"></i>
                  {t('链上数字权益', 'On-chain Digital Rights')}
                </div>
                <div className="text-5xl font-black text-[#A78BFA] mb-2">Token</div>
                <div className="text-[#A78BFA]/70 text-sm">On-chain Digital Token</div>
                <div className="mt-3 space-y-1.5">
                  {[
                    t('可拆分 → 百元起投', 'Divisible → Low entry threshold'),
                    t('可流转 → 7×24全球', 'Transferable → 24/7 global'),
                    t('可编程 → 自动分红', 'Programmable → Auto dividends'),
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-end gap-2 text-white/60 text-xs">
                      <i className="ri-checkbox-circle-fill text-[#A78BFA] text-xs"></i>
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Asset Types */}
          <h3 className="text-xl font-black text-gray-800 mb-2 flex items-center gap-2">
            <div className="w-1 h-5 bg-[#7C3AED] rounded-full" />
            {t('十大主要可代币化资产类型', 'Ten Major Tokenizable Asset Types')}
          </h3>
          <p className="text-gray-400 text-sm mb-5 ml-3">
            {t('含2月6日新政重点支持的供应链金融、新能源/光伏、碳资产等赛道', 'Including supply chain finance, clean energy/solar, and carbon assets highlighted in the Feb 6 policy')}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
            {assetTypes.map((asset) => (
              <div
                key={asset.name}
                className="group bg-white border border-gray-100 rounded-xl p-5 hover:-translate-y-1 transition-all cursor-default"
                style={{ borderLeft: `3px solid ${asset.color}` }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ background: `${asset.color}15` }}>
                    <i className={`${asset.icon} text-xl`} style={{ color: asset.color }}></i>
                  </div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold leading-tight text-center" style={{ background: `${asset.color}12`, color: asset.color }}>
                    {asset.tag}
                  </span>
                </div>
                <h4 className="font-black text-gray-800 mb-1.5 text-sm">{asset.name}</h4>
                <p className="text-gray-500 text-xs leading-relaxed mb-2 line-clamp-3">{asset.desc}</p>
                <div className="text-[10px] text-gray-400 italic mb-2 line-clamp-1">{t('试点：', 'Pilot: ')}{asset.pilot}</div>
                <div className="flex items-center justify-between pt-2 border-t border-gray-50">
                  <div className="text-xs text-gray-400">{t('全球规模', 'Global Scale')}</div>
                  <div className="text-xs font-black" style={{ color: asset.color }}>{asset.tvl}</div>
                </div>
              </div>
            ))}
          </div>

          {/* ── 证件凭证上链专题 ── */}
          <div className="mb-14 rounded-2xl overflow-hidden border border-[#EC4899]/20" style={{ background: 'linear-gradient(135deg, #1A0050, #2E1065)' }}>
            <div className="px-6 pt-6 pb-4 border-b border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 flex items-center justify-center bg-[#EC4899]/20 rounded-xl">
                  <i className="ri-file-shield-line text-[#EC4899] text-lg"></i>
                </div>
                <div>
                  <h3 className="text-white font-black text-base">{t('证件与凭证上链：非融资类RWA的未来大趋势', 'Credentials On-Chain: The Future Trend of Non-Financial RWA')}</h3>
                  <p className="text-white/50 text-xs">{t('从英国房产证全面上链，到全球数字身份革命——这是大势所趋', 'From UK land registry to global digital identity revolution — this is inevitable')}</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <p className="text-white/70 text-sm leading-relaxed mb-5">
                {t(
                  'RWA的应用范畴远不止于融资类资产。<strong className="text-white">个人身份证件、学位证书、房产证、结婚证、营业执照、专业资格证……所有这些证件凭证，都将在未来陆续上链</strong>——不是为了融资，而是作为不可篡改的电子凭证，彻底解决证件造假、异地核验、政府信息孤岛等难题。届时，办事无需携带任何实体证件，一个手机扫码即可在全球任何地方完成身份核验。这不是科幻，这是正在发生的现实。',
                  'The scope of RWA extends far beyond financial assets. Personal IDs, diplomas, property deeds, marriage certificates, business licenses, professional qualifications — all these credentials will eventually be on-chain, not for financing, but as tamper-proof digital records. This is not science fiction; it is already happening.'
                )}
              </p>
              {/* Category Tabs */}
              <div className="flex flex-wrap gap-2 mb-5">
                {credentialOnChainData.map((cat, i) => (
                  <button
                    key={cat.category}
                    onClick={() => setActiveCredential(i)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${activeCredential === i ? 'bg-[#EC4899] text-white' : 'bg-white/10 text-white/60 hover:bg-white/15'}`}
                  >
                    <i className={cat.icon}></i>
                    {cat.category}
                  </button>
                ))}
              </div>
              {/* Active Category Detail */}
              <div className="grid lg:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-[#EC4899] text-xs font-bold mb-3 flex items-center gap-1.5">
                    <i className="ri-earth-line"></i>{t('全球先行案例', 'Global Pioneer Cases')}
                  </h4>
                  <div className="space-y-3">
                    {credentialOnChainData[activeCredential].globalCases.map((c) => (
                      <div key={c.country} className="bg-white/5 border border-white/10 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-white font-bold text-xs px-2 py-0.5 bg-[#EC4899]/20 rounded-full">{c.country}</span>
                        </div>
                        <p className="text-white/65 text-xs leading-relaxed">{c.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-[#F59E0B] text-xs font-bold mb-3 flex items-center gap-1.5">
                    <i className="ri-flag-line"></i>{t('中国进展与实践', 'China Progress & Practice')}
                  </h4>
                  <div className="bg-white/5 border border-[#F59E0B]/20 rounded-xl p-4 h-full">
                    <p className="text-white/65 text-xs leading-relaxed">{credentialOnChainData[activeCredential].chinaCases}</p>
                  </div>
                </div>
              </div>
              {/* Future Outlook */}
              <div className="mt-5 p-4 bg-[#EC4899]/10 border border-[#EC4899]/20 rounded-xl">
                <div className="flex items-start gap-3">
                  <i className="ri-flashlight-line text-[#EC4899] text-base shrink-0 mt-0.5"></i>
                  <div>
                    <h5 className="text-white font-bold text-sm mb-1.5">{t('未来展望：证件全面上链的时代图景', 'Future Outlook: The Era of Universal Credential On-Chain')}</h5>
                    <p className="text-white/65 text-xs leading-relaxed">
                      {t(
                        '未来5-10年，以下场景将成为现实：①每位公民拥有唯一的数字身份链上凭证，替代现有身份证、护照等实体证件；②企业的全套证照统一上链，行政审批从"递材料"变为"扫码核验"；③学历、职业资格等凭证全球互认；④医疗、金融、法律等行业的资格证书链上存证；⑤电子化凭证体系将与RWA金融体系深度融合。这不仅仅是效率的提升，更是整个文明的信任基础设施重构。',
                        'In the next 5-10 years: ① Every citizen will have a unique on-chain digital identity replacing physical IDs and passports; ② All business licenses will be on-chain, turning administrative approvals from "submitting documents" to "scanning QR codes"; ③ Academic and professional credentials will be globally recognized; ④ Professional certifications in healthcare, finance, and law will be on-chain; ⑤ Digital credential systems will deeply integrate with RWA financial systems. This is not just an efficiency upgrade — it is a reconstruction of civilization\'s trust infrastructure.'
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Myths Debunking */}
          <div className="mb-12">
            <h3 className="text-xl font-black text-gray-800 mb-2 flex items-center gap-2">
              <div className="w-1 h-5 bg-[#EF4444] rounded-full" />
              {t('八大常见误区解毒', 'Eight Common Misconceptions Debunked')}
            </h3>
            <p className="text-gray-400 text-sm mb-6 ml-3">
              {t('这些误区正在误导大众，请认真阅读并传播正确认知', 'These misconceptions are misleading the public — please read carefully and spread correct understanding')}
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {displayMythes.map((m, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                  <div className="bg-red-50 border-b border-red-100 px-5 py-3 flex items-start gap-3">
                    <div className="w-7 h-7 flex items-center justify-center bg-[#EF4444] rounded-lg shrink-0 mt-0.5">
                      <i className="ri-close-line text-white text-sm"></i>
                    </div>
                    <div>
                      <div className="text-xs font-bold text-red-600 mb-0.5">❌ {t('误区', 'Myth')} {i + 1}：{m.dangerLevel}</div>
                      <div className="text-red-800 text-sm font-bold">{m.myth}</div>
                    </div>
                  </div>
                  <div className="px-5 py-3 flex items-start gap-3">
                    <div className="w-7 h-7 flex items-center justify-center bg-[#7C3AED] rounded-lg shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <div>
                      <div className="text-xs font-bold text-[#7C3AED] mb-1">✅ {t('正确认知', 'Correct Understanding')}</div>
                      <div className="text-gray-600 text-sm leading-relaxed">{m.reality}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {!showAllMythes && (
              <div className="mt-4 text-center">
                <button
                  onClick={() => setShowAllMythes(true)}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#EF4444]/8 border border-[#EF4444]/20 text-[#EF4444] text-sm font-bold rounded-full hover:bg-[#EF4444]/15 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  {t('展开查看全部8大误区', 'Show All 8 Misconceptions')}
                </button>
              </div>
            )}
          </div>

          {/* Audience Value */}
          <div className="mb-12">
            <h3 className="text-xl font-black text-gray-800 mb-2 flex items-center gap-2">
              <div className="w-1 h-5 bg-[#F59E0B] rounded-full" />
              {t('RWA99.cn 对不同人群的价值', 'Value of RWA99.cn for Different Audiences')}
            </h3>
            <p className="text-gray-400 text-sm mb-6 ml-3">
              {t('无论你是哪类读者，本站都能帮你找到最有价值的信息', 'Whatever type of reader you are, this site helps you find the most valuable information')}
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {audienceValue.map((a) => (
                <div
                  key={a.group}
                  className="rounded-xl p-5 border"
                  style={{ background: `${a.color}06`, borderColor: `${a.color}20` }}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl mb-3" style={{ background: `${a.color}18` }}>
                    <i className={`${a.icon} text-xl`} style={{ color: a.color }}></i>
                  </div>
                  <div className="font-black text-gray-800 text-sm mb-3">{a.group}</div>
                  <ul className="space-y-1.5">
                    {a.values.map((v, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs text-gray-600 leading-relaxed">
                        <i className="ri-arrow-right-s-line mt-0.5 shrink-0" style={{ color: a.color }}></i>
                        {v}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* ── Enterprise RWA Self-Test ── */}
          <RWASelfTest />

          {/* 2026 Hard Thresholds */}
          <div
            className="rounded-2xl p-6 lg:p-8"
            style={{ background: 'linear-gradient(135deg, #2E1065, #4C1D95)' }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-[#F59E0B]/20 rounded-xl">
                <i className="ri-calendar-2-line text-[#F59E0B] text-xl"></i>
              </div>
              <div>
                <div className="text-[#F59E0B] font-bold">{t('2026 RWA元年', '2026 RWA Milestone Year')}</div>
                <div className="text-white text-sm font-black">{t('参与 RWA 的核心门槛与基准数据', 'Core Thresholds & Benchmarks for RWA Participation')}</div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {thresholds2026.map((th, i) => (
                <div key={i} className="bg-white/8 border border-white/10 rounded-xl p-4 text-center hover:bg-white/12 transition-all">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg mx-auto mb-3" style={{ background: `${th.color}20` }}>
                    <i className={`${th.icon} text-lg`} style={{ color: th.color }}></i>
                  </div>
                  <div className="text-xs text-white/50 mb-1">{th.label}</div>
                  <div className="font-black text-white text-lg mb-1" style={{ color: th.color }}>{th.value}</div>
                  <div className="text-white/40 text-xs leading-relaxed">{th.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ======= 02 区块链基础 ======= */}
      <section id="blockchain" className="py-20 bg-[#F5F3FF]">
        <div className="max-w-[1440px] mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#7C3AED]/8 border border-[#7C3AED]/15 rounded-full mb-4">
              <i className="ri-links-line text-[#7C3AED] text-sm"></i>
              <span className="text-[#7C3AED] text-sm font-semibold">{t('02 · 区块链技术基础', '02 · Blockchain Fundamentals')}</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-black text-gray-900 mb-4">{t('RWA 必懂的区块链核心概念', 'Essential Blockchain Concepts for RWA')}</h2>
            <p className="text-gray-500 max-w-xl mx-auto text-base">
              {t('6个核心概念，帮你建立完整的RWA技术知识框架，不懂技术也能看懂。', '6 core concepts to build a complete RWA technical knowledge framework — no technical background required.')}
            </p>
          </div>

          {/* Video Player */}
          <div className="mb-10">
            <VideoPlayer
              title={t('「区块链与RWA」入门视频讲解', '"Blockchain & RWA" Introductory Video')}
              subtitle={t('专题视频 · 零基础也能看懂', 'Thematic Video · Beginner-friendly')}
              duration={t('约20分钟', 'About 20 min')}
              heightClass="h-72"
              theme="dark"
              className="border border-[#7C3AED]/20"
            />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {blockchainBasics.map((item) => (
              <div
                key={item.step}
                className="group bg-white border border-gray-100 rounded-xl p-6 hover:border-[#7C3AED]/30 hover:-translate-y-0.5 transition-all"
              >
                <div className="flex items-start gap-4 mb-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#7C3AED]/8 rounded-xl shrink-0 group-hover:bg-[#7C3AED]/15 transition-colors">
                    <i className={`${item.icon} text-[#7C3AED] text-2xl`}></i>
                  </div>
                  <div className="text-5xl font-black text-[#7C3AED]/10 group-hover:text-[#7C3AED]/20 transition-colors leading-none">{item.step}</div>
                </div>
                <h4 className="font-black text-gray-800 mb-2">{item.title}</h4>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
